import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { List, X, Lock } from '@phosphor-icons/react';
import { Input } from './ui/input';
import { Button } from './ui/button';

const navLinks = [
  { path: '/', label: 'Home', labelEs: 'Inicio' },
  { path: '/itinerary', label: 'Itinerary', labelEs: 'Itinerario' },
  { path: '/accommodations', label: 'Stay', labelEs: 'Hospedaje' },
  { path: '/travel', label: 'Travel', labelEs: 'Viaje' },
  { path: '/rsvp', label: 'RSVP', labelEs: 'Confirmar' },
  { path: '/flights', label: 'Travel Info', labelEs: 'Info Viaje' },
  { path: '/registry', label: 'Registry', labelEs: 'Regalos' },
  { path: '/faq', label: 'FAQ', labelEs: 'Preguntas' },
];

// Site password - change this to your desired password
const SITE_PASSWORD = 'Arya2026';

export default function Layout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const location = useLocation();

  useEffect(() => {
    // Check if user has already entered the password
    const hasAccess = localStorage.getItem('wedding_site_access');
    if (hasAccess === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === SITE_PASSWORD) {
      setIsAuthenticated(true);
      localStorage.setItem('wedding_site_access', 'true');
      setError('');
    } else {
      setError('Incorrect password. Please try again.');
    }
  };

  // Show password gate if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          {/* Circular Photo */}
          <div className="mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_2a6383c9-47ae-4f9d-80c4-df64c0760867/artifacts/qz8lgnbp_closes%20up.jpg"
              alt="Stefanie and Walter"
              className="w-32 h-32 rounded-full object-cover mx-auto shadow-lg"
              style={{ border: '4px solid #F0EBE3' }}
            />
          </div>
          
          <h1 className="font-display text-3xl md:text-4xl mb-2" style={{ color: '#C9B99A' }}>
            Stefanie & Walter
          </h1>
          <p className="font-body text-stone-500 mb-2">
            Please enter the password to view our wedding website
          </p>
          <p className="font-body text-sm italic text-stone-400 mb-8">
            Por favor ingresa la contraseña para ver nuestro sitio web de boda
          </p>
          
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div className="relative">
              <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password / Contraseña"
                className="pl-10 bg-white border-stone-200 focus:border-[#C9B99A] text-center"
                data-testid="site-password-input"
              />
            </div>
            {error && (
              <div>
                <p className="text-red-500 text-sm">Incorrect password. Please try again.</p>
                <p className="text-red-400 text-xs italic">Contraseña incorrecta. Inténtalo de nuevo.</p>
              </div>
            )}
            <Button
              type="submit"
              data-testid="site-password-submit"
              className="w-full py-6 text-white font-body"
              style={{ backgroundColor: '#C9B99A' }}
            >
              Enter / Entrar
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Navigation */}
      <nav
        data-testid="main-navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          mobileMenuOpen ? 'bg-white shadow-sm' : (scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent')
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link
              to="/"
              data-testid="logo-link"
              className="font-display text-lg md:text-xl text-stone-900"
            >
              S <span className="italic" style={{ color: '#B8A88A' }}>&</span> W
            </Link>

            {/* Desktop Navigation - Bilingual */}
            <div className="hidden md:flex items-center space-x-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                  className="font-body text-sm tracking-wide transition-colors text-center"
                  style={{ color: isActive(link.path) ? '#B8A88A' : '#57534E' }}
                >
                  <span className="block">{link.label}</span>
                  <span className="block text-xs italic text-stone-400">{link.labelEs}</span>
                </Link>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-toggle"
              className="md:hidden p-2 text-stone-900"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <List size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu - Always solid white background */}
        {mobileMenuOpen && (
          <div
            data-testid="mobile-menu"
            className="md:hidden fixed inset-0 top-16 bg-white z-40"
            style={{ backgroundColor: '#ffffff' }}
          >
            <div className="flex flex-col items-center justify-center h-full space-y-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  className={`text-2xl font-display text-center ${
                    isActive(link.path) ? 'text-[#B8A88A]' : 'text-stone-600'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="block">{link.label}</span>
                  <span className="block text-sm italic text-stone-400">{link.labelEs}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="pt-16 md:pt-20">
        <Outlet />
      </main>

      {/* Footer */}
      <footer data-testid="footer" className="bg-stone-900 text-stone-400 py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <p className="font-display text-2xl text-white mb-2">
            Stefanie <span className="italic" style={{ color: '#C9B99A' }}>&</span> Walter
          </p>
          <p className="font-body text-sm tracking-wide mb-1">
            December 11, 2026 • Coatapeque, El Salvador
          </p>
          <p className="font-body text-xs italic text-stone-500">
            11 de Diciembre, 2026
          </p>
          <div className="mt-8 pt-8 border-t border-stone-800">
            <Link
              to="/admin"
              data-testid="admin-link"
              className="text-xs text-stone-600 hover:text-stone-400 transition-colors"
            >
              Admin
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
